import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled, { createGlobalStyle, css } from "styled-components";
import { Card, Button, Row, Col, Container, Form } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { OrangeButton, CloseIcon } from "../components/DefaultImports.js";
import { CustomModal } from "./CustomModal.js";
import {
  createTables,
  fetchTables,
  createPlaceOfTable,
  fetchPlaceOfTable,
  bulkUpdateTables,
  fetchTableConnections,
  updateTableConnections,
  fetchRestaurantDetails,
} from "../actions/restaurantActions";
import { api } from "../App";
import DropDownButton from "../components/DropDownButton.js";

/* ---------- styles ---------- */

const palette = {
  orange: "#EE754C",
  orangeDark: "#D95F38",
  orangeSoft: "#FFF3EE",
  sage: "#99B0AD",
  sageDark: "#6F8F8B",
  ink: "#433034",
  text: "#5F5557",
  muted: "#847B7D",
  border: "#E8E2E0",
  surface: "#FFFFFF",
  surfaceSoft: "#FBF9F7",
  cream: "#FAF6E9",
  danger: "#C94F4F",
};

const controlStyles = css`
  min-height: 34px;
  height: 34px;
  border: 1px solid ${palette.border};
  border-radius: 6px;
  box-shadow: none;
  font-size: 0.82rem;
  color: ${palette.ink};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus {
    border-color: ${palette.orange};
    box-shadow: 0 0 0 2px rgba(238, 117, 76, 0.12);
  }
`;

const buttonStyles = css`
  width: 136px;
  min-width: 136px;
  min-height: 34px;
  padding: 6px 10px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:not(:disabled):hover {
    box-shadow: 0 2px 6px rgba(67, 48, 52, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  @media (max-width: 576px) {
    width: 100%;
    min-width: 0;
    min-height: 38px;
    padding: 6px 7px;
    white-space: normal;
  }
`;

const PrimaryButton = styled(OrangeButton)`
  ${buttonStyles}
`;

const SecondaryButton = styled(Button)`
  ${buttonStyles}
  background: ${palette.surface};
  border-color: ${palette.sage};
  color: ${palette.sageDark};

  &:hover,
  &:focus,
  &:active {
    background: #f1f6f5 !important;
    border-color: ${palette.sageDark} !important;
    color: ${palette.sageDark} !important;
  }
`;

const DangerButton = styled(Button).attrs({ variant: "danger" })`
  ${buttonStyles}
  background: ${palette.surface};
  border-color: #e7aaaa;
  color: #ffffff;

  &:hover,
  &:focus,
  &:active {
    background: #fff1f1 !important;
    border-color: ${palette.danger} !important;
    color: ${palette.danger} !important;
  }
`;

const CompactButton = styled(SecondaryButton)`
  width: 160px;
  min-width: 160px;
`;

const TextButton = styled(Button)`
  width: auto;
  min-width: 0;
  min-height: 24px;
  padding: 2px 0;
  color: ${palette.orange};
  font-size: 0.76rem;
  font-weight: 700;
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${palette.orangeDark};
    text-decoration: underline;
  }
`;

const DropdownGlobalStyles = createGlobalStyle`
  .form-control-people {
    width: 100%;
    height: 34px;
    display: flex;
    overflow: hidden;
    border: 1px solid ${palette.border};
    border-radius: 6px;
    background: ${palette.surface};
    box-shadow: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .form-control-people:hover {
    border-color: ${palette.sage};
  }

  .form-control-people:focus-within {
    border-color: ${palette.orange};
    box-shadow: 0 0 0 2px rgba(238, 117, 76, 0.12);
  }

  .custom-dropdown-menu {
    max-height: 220px;
    overflow-y: auto;
    margin-top: 4px;
    padding: 4px;
    border: 1px solid ${palette.border};
    border-radius: 7px;
    background: ${palette.surface};
    box-shadow: 0 14px 35px rgba(67, 48, 52, 0.16);
  }

  .dropdown-menu-item {
    padding: 7px 9px;
    border-radius: 5px;
    color: ${palette.text};
    font-size: 0.8rem;
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease;
  }

  .dropdown-menu-item:hover {
    background: ${palette.orangeSoft};
    color: ${palette.orangeDark};
  }

  .dropdown-menu-item.selected {
    background: ${palette.orangeSoft};
    color: ${palette.orangeDark};
    font-weight: 700;
  }
`;

const StyledCard = styled(Card)`
  margin: 10px 0;
  border: 1px solid ${palette.border};
  box-shadow: 0 4px 12px rgba(67, 48, 52, 0.07);
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 576px) {
    margin: 5px 0;
    border-radius: 8px;
  }
`;
const CardTop = styled.div`
  padding: 12px 14px;
  background: linear-gradient(135deg, ${palette.cream} 0%, #fffaf5 100%);
  border-bottom: 1px solid ${palette.border};
  position: relative;

  @media (max-width: 576px) {
    padding: 9px 10px;
  }
`;
const CardBodyBox = styled.div`
  background: ${palette.surfaceSoft};
  padding: 14px;

  @media (max-width: 576px) {
    padding: 9px;
  }
`;
const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;

  @media (max-width: 576px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;

    > * {
      width: 100%;
      min-width: 0;
      max-width: none;
    }
  }
`;
const RoomControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  justify-content: flex-end;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-top: 10px;
    justify-content: flex-start;
  }

  @media (max-width: 576px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
    margin-top: 7px;

    > * {
      width: 100%;
      min-width: 0;
    }

    > :first-child {
      grid-column: 1 / -1;
    }
  }
`;
const RoomTitle = styled.div`
  font-size: 0.96rem;
  font-weight: 800;
  color: ${palette.ink};
  letter-spacing: -0.01em;
`;
const RoomSubtitle = styled.div`
  font-size: 0.78rem;
  color: ${palette.muted};
  margin-top: 4px;

  @media (max-width: 576px) {
    margin-top: 2px;
  }
`;
const SelectedRoomSummary = styled.div`
  margin-bottom: 10px;

  @media (max-width: 576px) {
    margin-bottom: 6px;
  }
`;
const MainActions = styled.div`
  margin-bottom: 10px;

  @media (max-width: 576px) {
    margin-bottom: 7px;
  }
`;
const EmptyState = styled.div`
  border: 1px dashed ${palette.sage};
  border-radius: 8px;
  padding: 18px 14px;
  font-size: 0.82rem;
  text-align: center;
  color: ${palette.muted};
  background: ${palette.surface};

  @media (max-width: 576px) {
    padding: 12px 9px;
  }
`;
const TableListScroll = styled.div`
  max-height: clamp(260px, 55vh, 600px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #b8b8b8;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  scrollbar-width: thin;
  scrollbar-color: #b8b8b8 #f1f1f1;
`;
const TableList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;

  @media (max-width: 576px) {
    gap: 5px;
  }
`;
const TableRowCard = styled.div`
  border: 1px solid ${palette.border};
  border-radius: 8px;
  padding: 9px 11px;
  background: ${palette.surface};
  box-shadow: 0 3px 10px rgba(67, 48, 52, 0.04);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    border-color: #d8cdca;
    box-shadow: 0 3px 9px rgba(67, 48, 52, 0.07);
  }

  @media (max-width: 576px) {
    padding: 7px 8px;
  }
`;
const TableRowGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 130px 44px;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr) auto 44px;
    gap: 10px;
  }

  @media (max-width: 576px) {
    grid-template-columns: minmax(0, 1fr) auto 40px;
    gap: 4px 8px;
  }
`;
const DeleteCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }

  @media (max-width: 576px) {
    justify-content: flex-end;
  }
`;
const SmallInfo = styled.div`
  font-size: 0.74rem;
  color: ${palette.muted};
  margin-top: 2px;
`;
const AddFilterBox = styled.button`
  width: 100%;
  min-height: 34px;
  padding: 6px 10px;
  border: 1px dashed ${palette.sage};
  border-radius: 7px;
  background: ${palette.surface};
  color: ${palette.sageDark};
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.8rem;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${palette.orange};
    color: ${palette.orange};
    background: ${palette.orangeSoft};
  }
`;
const BulkTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
`;

const DropdownField = styled.div`
  width: 100%;
  min-width: 0;

  .form-control-people {
    cursor: pointer;
  }
`;

const FieldLabel = styled.label`
  display: block;
  margin: 0 0 4px;
  color: ${palette.ink};
  font-size: 0.76rem;
  font-weight: 700;
`;

const PageRoomPicker = styled(DropdownField)`
  flex: 0 1 210px !important;
  min-width: 180px;
  max-width: 210px;

  @media (max-width: 576px) {
    min-width: 100%;
    max-width: none;
  }
`;

const SectionCard = styled.section`
  margin-bottom: 10px;
  padding: 11px;
  border: 1px solid ${palette.border};
  border-radius: 8px;
  background: ${palette.surface};

  @media (max-width: 576px) {
    margin-bottom: 7px;
    padding: 8px;
  }
`;

const EditorSettingsGrid = styled.div`
  display: grid;
  /* TEMPORARILY HIDDEN: assignment mode. Restore the two-column layout when re-enabling it. */
  /* grid-template-columns: minmax(220px, 0.8fr) minmax(280px, 1.2fr); */
  grid-template-columns: minmax(220px, 1fr);
  gap: 10px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 576px) {
    gap: 7px;
  }
`;

const HelpText = styled.div`
  margin-top: 4px;
  color: ${palette.muted};
  font-size: 0.74rem;
  line-height: 1.35;
`;

const GroupToolbar = styled(SectionCard)`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  background: ${palette.cream};

  @media (max-width: 768px) {
    align-items: stretch;
    flex-direction: column;
  }

  @media (max-width: 576px) {
    gap: 7px;
  }
`;

const GroupToolbarActions = styled.div`
  display: flex;
  gap: 7px;
  align-items: flex-end;
  flex-wrap: wrap;

  ${DropdownField} {
    min-width: 170px;
    flex: 1 1 170px;
  }

  @media (max-width: 576px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

    > * {
      width: 100%;
      min-width: 0;
      max-width: none;
    }

    ${DropdownField} {
      grid-column: 1 / -1;
    }
  }
`;

const BulkHeader = styled.div`
  display: grid;
  /* TEMPORARILY HIDDEN: Mesa, Formato + Junções columns. */
  /* grid-template-columns: 65px minmax(150px, 1fr) 95px 115px 150px 65px; */
  grid-template-columns: minmax(150px, 1fr) 95px 65px;
  gap: 8px;
  min-width: 400px;
  padding: 8px 9px;
  border: 1px solid ${palette.border};
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  background: ${palette.cream};
  color: ${palette.ink};
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.035em;
`;

const BulkRows = styled.div`
  min-width: 400px;
  max-height: 390px;
  overflow-y: auto;
  padding: 5px;
  border: 1px solid ${palette.border};
  border-radius: 0 0 8px 8px;
  background: ${palette.surfaceSoft};
`;

const BulkRow = styled.div`
  display: grid;
  /* TEMPORARILY HIDDEN: Mesa, Formato + Junções columns. */
  /* grid-template-columns: 65px minmax(150px, 1fr) 95px 115px 150px 65px; */
  grid-template-columns: minmax(150px, 1fr) 95px 65px;
  gap: 8px;
  align-items: center;
  min-width: 380px;
  margin-bottom: 4px;
  padding: 6px 7px;
  border: 1px solid ${palette.border};
  border-radius: 6px;
  background: ${palette.surface};

  &:last-of-type {
    margin-bottom: 0;
  }

  .form-control {
    ${controlStyles}
  }
`;

const ConnectionsCell = styled.div`
  color: ${palette.text};
  font-size: 0.74rem;
  line-height: 1.35;
`;

const DeleteIconButton = styled.button`
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: ${palette.sageDark};
  cursor: pointer;
  transition:
    color 0.15s ease,
    border-color 0.15s ease,
    background-color 0.15s ease;

  &:hover {
    border-color: #f0c4c4;
    background: #fff2f2;
    color: ${palette.danger};
  }
`;

const StyledModal = styled(CustomModal)`
  .modal-dialog {
    width: calc(100% - 16px);
    max-width: ${({ $wide }) => ($wide ? "900px" : "500px")};
  }

  .modal-body {
    color: ${palette.text};
  }

  .modal-footer > * {
    margin: 0;
  }

  .form-label {
    margin-bottom: 4px;
    color: ${palette.ink};
    font-size: 0.76rem;
    font-weight: 700;
  }

  .form-control {
    ${controlStyles}
  }

  @media (max-width: 576px) {
    .modal-dialog {
      width: calc(100% - 12px);
      margin: auto;
      padding: 3px;
    }

    .modal-body {
      padding: 9px;
    }

    .modal-footer {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px;
      padding: 8px 9px;
    }

    .modal-footer > * {
      width: 100%;
      min-width: 0;
      max-width: none;
    }

    .modal-footer > :last-child:nth-child(3) {
      grid-column: 1 / -1;
    }
  }
`;

const ModalFooterButton = styled(SecondaryButton)`
  width: 136px;
`;

const ModalFooterPrimaryButton = styled(PrimaryButton)`
  width: 136px;
`;

const ModalFooterDangerButton = styled(DangerButton)`
  width: 136px;
`;

const normalizeAutoSetTables = (value) => {
  if (value === false || value === 0 || value === "false" || value === "0") {
    return false;
  }

  return true;
};

const assignmentModeOptions = ["Automático flexível", "Grupos de mesas fixos"];

/* ---------- component ---------- */

const TablesWithoutDragging = ({ all_tables = [] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const restaurantZones = useSelector((state) => state.restaurantZones);
  const { zones = [] } = restaurantZones;
  const restaurantDetails = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurantDetails;
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const tableConnectionsState = useSelector((state) => state.tableConnections);
  const { connections: tableConnections = [] } = tableConnectionsState;
  const [tables, setTables] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState("");

  /* modals */
  const [showCreateTables, setShowCreateTables] = useState(false);
  const [showDeleteTables, setShowDeleteTables] = useState(false);
  const [showCreateZone, setShowCreateZone] = useState(false);
  const [showDeleteZone, setShowDeleteZone] = useState(false);
  const [showBulkEditor, setShowBulkEditor] = useState(false);

  /* create tables */
  const [numTablesToAdd, setNumTablesToAdd] = useState("");
  const [numSeatsPerTable, setNumSeatsPerTable] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  /* delete tables */
  const [numTablesToDelete, setNumTablesToDelete] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  /* create zone */
  const [zoneName, setZoneName] = useState("");
  const [isCreatingZone, setIsCreatingZone] = useState(false);

  /* delete zone */
  const [isDeletingZone, setIsDeletingZone] = useState(false);

  /* bulk editor */
  const [bulkEditPlace, setBulkEditPlace] = useState(null);
  const [bulkRows, setBulkRows] = useState([]);
  const [isBulkEditorSaving, setIsBulkEditorSaving] = useState(false);
  const [currentAutoSetTables, setCurrentAutoSetTables] = useState(true);
  const [draftAutoSetTables, setDraftAutoSetTables] = useState(true);

  /* table connections */
  const [currentConnectionTableId, setCurrentConnectionTableId] =
    useState(null);
  const [selectedConnectionIds, setSelectedConnectionIds] = useState([]);
  const [isConnectionConfigMode, setIsConnectionConfigMode] = useState(false);
  const [pendingConnectionChanges, setPendingConnectionChanges] = useState({});
  const [isBulkDirty, setIsBulkDirty] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  /* single delete */
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    tableId: null,
    label: "",
  });
  const tableShapeOptions = useMemo(
    () => [
      t("ui.components.tablesWithoutDragging.quadrada"),
      t("ui.components.tablesWithoutDragging.redonda"),
    ],
    [t],
  );

  const isAutoSetTablesDirty =
    normalizeAutoSetTables(draftAutoSetTables) !==
    normalizeAutoSetTables(currentAutoSetTables);

  const hasUnsavedBulkModalChanges =
    isBulkDirty ||
    isAutoSetTablesDirty ||
    Object.keys(pendingConnectionChanges).length > 0;

  useEffect(() => {
    setTables(all_tables || []);
  }, [all_tables]);
  useEffect(() => {
    if (zones.length > 0 && !selectedPlace) {
      setSelectedPlace(zones[0].id);
    }
  }, [zones, selectedPlace]);
  useEffect(() => {
    if (restaurants?.id) {
      dispatch(fetchTableConnections(restaurants.id));
    }
  }, [dispatch, restaurants?.id]);
  useEffect(() => {
    setCurrentAutoSetTables(
      normalizeAutoSetTables(restaurants?.auto_set_tables),
    );
  }, [restaurants?.auto_set_tables]);
  useEffect(() => {
    if (showBulkEditor && bulkEditPlace) {
      const rows = (tables || [])
        .filter((table) => table.place_of_table === bulkEditPlace)
        .sort((a, b) => Number(a.table_number) - Number(b.table_number));

      setBulkRows(rows.map((row) => ({ ...row })));
    }
  }, [tables, showBulkEditor, bulkEditPlace]);
  const selectedZone = useMemo(
    () => zones.find((z) => z.id === selectedPlace),
    [zones, selectedPlace],
  );
  const bulkSelectedZone = useMemo(
    () => zones.find((zone) => Number(zone.id) === Number(bulkEditPlace)),
    [zones, bulkEditPlace],
  );
  const zoneDropdownOptions = useMemo(
    () => zones.map((zone) => zone.place_of_table),
    [zones],
  );
  const tablesInSelectedZone = useMemo(() => {
    return (tables || [])
      .filter((t) => t.place_of_table === selectedPlace)
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));
  }, [tables, selectedPlace]);
  const currentConnectionTable = useMemo(() => {
    if (!currentConnectionTableId) return null;
    return (
      bulkRows.find(
        (table) => Number(table.id) === Number(currentConnectionTableId),
      ) || null
    );
  }, [bulkRows, currentConnectionTableId]);
  const getBulkTableDropdownLabel = useCallback((table) => {
    if (!table) return "";
    return t("ui.i18nFinalCleanup.tableLabel", { label: table.table_number || table.table_name || table.id });
  }, [t]);
  const bulkTableDropdownOptions = useMemo(
    () => bulkRows.map((table) => getBulkTableDropdownLabel(table)),
    [bulkRows, getBulkTableDropdownLabel],
  );
  const canCreateTables =
    !!restaurants?.id &&
    !!selectedPlace &&
    Number(numTablesToAdd) > 0 &&
    Number(numSeatsPerTable) > 0;
  const getConnectedTableIds = useCallback(
    (tableId) => {
      if (!tableId || !Array.isArray(tableConnections)) {
        return [];
      }
      return tableConnections
        .filter((connection) => {
          return (
            connection.is_active &&
            (connection.table_a === tableId || connection.table_b === tableId)
          );
        })
        .map((connection) => {
          return connection.table_a === tableId
            ? connection.table_b
            : connection.table_a;
        });
    },
    [tableConnections],
  );

  const getTableLabelById = useCallback(
    (tableId) => {
      const table = (tables || []).find(
        (candidate) => Number(candidate.id) === Number(tableId),
      );

      if (!table) return `#${tableId}`;

      return table.table_name || table.table_number || `#${table.id}`;
    },
    [tables],
  );

  const getEffectiveConnectedTableIds = useCallback(
    (tableId, draftMap = pendingConnectionChanges) => {
      if (!tableId) return [];

      const normalizedTableId = Number(tableId);
      const hasOwnDraft = Object.prototype.hasOwnProperty.call(
        draftMap,
        normalizedTableId,
      );
      const baseIds = hasOwnDraft
        ? draftMap[normalizedTableId] || []
        : getConnectedTableIds(normalizedTableId);
      const result = new Set(baseIds.map(Number));

      Object.entries(draftMap).forEach(([sourceIdRaw, connectedIds]) => {
        const sourceId = Number(sourceIdRaw);

        if (sourceId === normalizedTableId) return;

        if ((connectedIds || []).map(Number).includes(normalizedTableId)) {
          result.add(sourceId);
        } else {
          result.delete(sourceId);
        }
      });

      result.delete(normalizedTableId);
      return Array.from(result);
    },
    [pendingConnectionChanges, getConnectedTableIds],
  );

  const getConnectedTableNames = useCallback(
    (tableId) => {
      const ids = getEffectiveConnectedTableIds(tableId);

      if (!ids.length) return "Nenhuma";

      return ids.map((id) => getTableLabelById(id)).join(", ");
    },
    [getEffectiveConnectedTableIds, getTableLabelById],
  );

  const startEditingConnections = useCallback(
    (table) => {
      if (!table?.id) return;

      const nextDraftMap = { ...pendingConnectionChanges };

      if (currentConnectionTableId && isConnectionConfigMode) {
        nextDraftMap[currentConnectionTableId] = selectedConnectionIds;
      }

      setPendingConnectionChanges(nextDraftMap);
      setCurrentConnectionTableId(table.id);
      setSelectedConnectionIds(
        getEffectiveConnectedTableIds(table.id, nextDraftMap),
      );
      setIsConnectionConfigMode(true);
    },
    [
      pendingConnectionChanges,
      currentConnectionTableId,
      selectedConnectionIds,
      isConnectionConfigMode,
      getEffectiveConnectedTableIds,
    ],
  );

  const handleStartGroupConfig = () => {
    const selectedTableInCurrentRoom = bulkRows.find(
      (row) => Number(row.id) === Number(currentConnectionTableId),
    );
    const tableToEdit = selectedTableInCurrentRoom || bulkRows[0];

    if (tableToEdit) {
      startEditingConnections(tableToEdit);
    }
  };

  const handleSelectedRoomChange = (roomName) => {
    const room = zones.find((zone) => zone.place_of_table === roomName);

    if (room) {
      setSelectedPlace(room.id);
    }
  };

  const handleBulkRoomChange = (roomName) => {
    const room = zones.find((zone) => zone.place_of_table === roomName);

    if (room) {
      changeBulkRoom(room.id);
    }
  };

  const handleConnectionTableChange = (tableLabel) => {
    const table = bulkRows.find(
      (row) => getBulkTableDropdownLabel(row) === tableLabel,
    );

    if (table) {
      startEditingConnections(table);
    }
  };

  const toggleConnectionTable = useCallback(
    (tableId) => {
      if (!currentConnectionTableId) return;

      const sourceId = Number(currentConnectionTableId);
      const targetId = Number(tableId);

      setPendingConnectionChanges((previousDraftMap) => {
        const currentSourceIds = getEffectiveConnectedTableIds(
          sourceId,
          previousDraftMap,
        );
        const nextSourceIds = currentSourceIds.includes(targetId)
          ? currentSourceIds.filter((id) => Number(id) !== targetId)
          : [...currentSourceIds, targetId];
        const nextDraftMap = {
          ...previousDraftMap,
          [sourceId]: nextSourceIds,
        };
        const currentTargetIds = getEffectiveConnectedTableIds(
          targetId,
          previousDraftMap,
        );

        if (nextSourceIds.includes(targetId)) {
          nextDraftMap[targetId] = Array.from(
            new Set([...currentTargetIds, sourceId]),
          ).filter((id) => Number(id) !== targetId);
        } else {
          nextDraftMap[targetId] = currentTargetIds.filter(
            (id) => Number(id) !== sourceId,
          );
        }

        setSelectedConnectionIds(nextSourceIds);
        return nextDraftMap;
      });
    },
    [currentConnectionTableId, getEffectiveConnectedTableIds],
  );

  const resetBulkEditorDrafts = () => {
    setIsBulkDirty(false);
    setPendingConnectionChanges({});
    setCurrentConnectionTableId(null);
    setSelectedConnectionIds([]);
    setIsConnectionConfigMode(false);
  };

  const forceCloseBulkEditor = () => {
    setShowUnsavedChangesModal(false);
    setShowBulkEditor(false);
    resetBulkEditorDrafts();
  };

  const requestCloseBulkEditor = () => {
    if (hasUnsavedBulkModalChanges) {
      setShowUnsavedChangesModal(true);
      return;
    }

    forceCloseBulkEditor();
  };

  /* ---------- handlers ---------- */

  const handleCreateTables = async () => {
    if (!canCreateTables) return;
    try {
      setIsCreating(true);
      const payload = {
        place_of_table: selectedPlace,
        num_tables_to_create: Number(numTablesToAdd),
        num_seats: Number(numSeatsPerTable),
      };
      await dispatch(createTables(restaurants.id, payload));
      await dispatch(fetchTables(restaurants.id));
      setShowCreateTables(false);
      setNumTablesToAdd("");
      setNumSeatsPerTable("");
    } catch (e) {
      console.error("Failed to create tables:", e);
    } finally {
      setIsCreating(false);
    }
  };
  const handleDeleteTables = async () => {
    const qty = Number(numTablesToDelete);
    if (!restaurants?.id || !selectedPlace || !qty || qty < 1) return;
    try {
      setIsDeleting(true);
      await api.delete(
        `restaurants/${restaurants.id}/zones/${selectedPlace}/delete-tables/${qty}/`,
      );
      await dispatch(fetchTables(restaurants.id));
      await dispatch(fetchTableConnections(restaurants.id));
      setShowDeleteTables(false);
      setNumTablesToDelete("");
    } catch (e) {
      console.error("Failed to delete tables:", e);
    } finally {
      setIsDeleting(false);
    }
  };
  const handleCreateZone = async () => {
    if (!restaurants?.id || !zoneName.trim()) return;
    try {
      setIsCreatingZone(true);
      const payload = {
        restaurant: restaurants.id,
        place_of_table: zoneName.trim(),
      };
      await dispatch(createPlaceOfTable(restaurants.id, payload));
      await dispatch(fetchPlaceOfTable(restaurants.id));
      setShowCreateZone(false);
      setZoneName("");
    } catch (e) {
      console.error("Failed to create zone:", e);
    } finally {
      setIsCreatingZone(false);
    }
  };
  const handleDeleteZone = async () => {
    if (!restaurants?.id || !selectedPlace) return;
    try {
      setIsDeletingZone(true);
      await api.delete(
        `restaurants/${restaurants.id}/placeoftable/${selectedPlace}/`,
      );
      setShowDeleteZone(false);
      setSelectedPlace("");
      resetBulkEditorDrafts();
      await dispatch(fetchPlaceOfTable(restaurants.id));
      await dispatch(fetchTables(restaurants.id));
      await dispatch(fetchTableConnections(restaurants.id));
    } catch (e) {
      console.error("Failed to delete zone:", e);
    } finally {
      setIsDeletingZone(false);
    }
  };
  const openBulkEditor = () => {
    const initialPlace = selectedPlace || (zones[0]?.id ?? null);

    setBulkEditPlace(initialPlace);

    const rows = (tables || [])
      .filter((t) => t.place_of_table === initialPlace)
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));

    setBulkRows(rows.map((r) => ({ ...r })));
    setDraftAutoSetTables(normalizeAutoSetTables(currentAutoSetTables));
    setShowBulkEditor(true);
    resetBulkEditorDrafts();
  };

  const changeBulkRoom = (placeId) => {
    if (hasUnsavedBulkModalChanges) {
      setShowUnsavedChangesModal(true);
      return;
    }

    setBulkEditPlace(placeId);

    const rows = (tables || [])
      .filter((t) => t.place_of_table === placeId)
      .sort((a, b) => Number(a.table_number) - Number(b.table_number));

    setBulkRows(rows.map((r) => ({ ...r })));
    resetBulkEditorDrafts();
  };

  const onBulkRowChange = (id, field, value) => {
    setIsBulkDirty(true);

    setBulkRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const saveBulkEditor = async ({ closeAfterSave = true } = {}) => {
    if (!restaurants?.id) return;

    try {
      setIsBulkEditorSaving(true);

      const updates = bulkRows.map((r) => {
        const u = { id: r.id };

        if (typeof r.table_name !== "undefined") {
          u.table_name = r.table_name;
        }

        if (typeof r.number_of_seats !== "undefined") {
          u.number_of_seats = Number(r.number_of_seats) || 0;
        }

        if (typeof r.round_or_square !== "undefined") {
          u.round_or_square = r.round_or_square || "square";
        }

        return u;
      });

      if (updates.length > 0) {
        await dispatch(bulkUpdateTables(restaurants.id, updates));
      }

      const normalizedDraftAutoSetTables =
        normalizeAutoSetTables(draftAutoSetTables);

      if (isAutoSetTablesDirty) {
        const response = await api.patch(
          `restaurants/details/${restaurants.id}/`,
          {
            auto_set_tables: normalizedDraftAutoSetTables,
          },
          {
            headers: {
              Authorization: `Bearer ${userInfo.token}`,
            },
          },
        );
        const updatedRestaurant = response.data;

        setCurrentAutoSetTables(
          normalizeAutoSetTables(updatedRestaurant.auto_set_tables),
        );
        setDraftAutoSetTables(
          normalizeAutoSetTables(updatedRestaurant.auto_set_tables),
        );
        await dispatch(fetchRestaurantDetails(restaurants.id));
      }

      const connectionEntries = Object.entries(pendingConnectionChanges);

      for (const [tableId, connectedIds] of connectionEntries) {
        await dispatch(
          updateTableConnections(
            restaurants.id,
            Number(tableId),
            connectedIds.map(Number),
          ),
        );
      }

      await dispatch(fetchTables(restaurants.id));
      await dispatch(fetchTableConnections(restaurants.id));

      setIsBulkDirty(false);
      setPendingConnectionChanges({});
      setSelectedConnectionIds([]);
      setShowUnsavedChangesModal(false);

      if (closeAfterSave) {
        setShowBulkEditor(false);
        setCurrentConnectionTableId(null);
        setIsConnectionConfigMode(false);
      }
    } catch (e) {
      console.error("Bulk editor save failed:", e);
    } finally {
      setIsBulkEditorSaving(false);
    }
  };
  const handleDeleteSingleTable = async (tableId) => {
    if (!restaurants?.id || !tableId) return;
    try {
      setDeletingRowId(tableId);
      await api.delete(`restaurants/${restaurants.id}/tables/${tableId}/`);
      await dispatch(fetchTables(restaurants.id));
      await dispatch(fetchTableConnections(restaurants.id));
    } catch (e) {
      console.error("Single table delete failed:", e);
    } finally {
      setDeletingRowId(null);
    }
  };
  return (
    <Container className="no-select">
      <DropdownGlobalStyles />
      <StyledCard>
        <CardTop>
          <Row className="align-items-center">
            <Col md={5}>
              <RoomTitle>
                {t("ui.components.tablesWithoutDragging.mesasPorSala")}
              </RoomTitle>
              <RoomSubtitle>
                {t(
                  "ui.components.tablesWithoutDragging.selecionaUmaSalaParaVerE",
                )}
              </RoomSubtitle>
            </Col>

            <Col md={7}>
              <RoomControls>
                <PageRoomPicker>
                  <FieldLabel>
                    {t("ui.components.tablesWithoutDragging.sala")}
                  </FieldLabel>
                  <DropDownButton
                    options={zoneDropdownOptions}
                    initialOption={selectedZone?.place_of_table || ""}
                    handleOptionChange={handleSelectedRoomChange}
                  />
                </PageRoomPicker>

                <PrimaryButton
                  data-setup-tour="rooms-create"
                  onClick={() => setShowCreateZone(true)}
                  disabled={!restaurants?.id}
                >
                  {t("ui.components.tablesWithoutDragging.novaSala")}
                </PrimaryButton>

                <DangerButton
                  onClick={() => setShowDeleteZone(true)}
                  disabled={!selectedPlace}
                >
                  {t("ui.components.tablesWithoutDragging.eliminarSala")}
                </DangerButton>
              </RoomControls>
            </Col>
          </Row>
        </CardTop>

        <CardBodyBox>
          {selectedZone ? (
            <>
              <SelectedRoomSummary>
                <RoomTitle>{selectedZone.place_of_table}</RoomTitle>
                <RoomSubtitle>
                  {(tablesInSelectedZone || []).length}
                  {t("ui.components.tablesWithoutDragging.mesaSNestaSala")}
                </RoomSubtitle>
              </SelectedRoomSummary>

              <MainActions>
                <ActionButtons
                  style={{
                    justifyContent: "flex-start",
                  }}
                >
                  <SecondaryButton
                    onClick={openBulkEditor}
                    disabled={!selectedPlace}
                  >
                    {t("ui.components.tablesWithoutDragging.editarLista")}
                  </SecondaryButton>

                  <PrimaryButton
                    onClick={() => setShowCreateTables(true)}
                    disabled={!selectedPlace}
                  >
                    {t("ui.components.tablesWithoutDragging.adicionarMesas")}
                  </PrimaryButton>

                  <DangerButton
                    onClick={() => setShowDeleteTables(true)}
                    disabled={!tablesInSelectedZone.length}
                  >
                    {t("ui.components.tablesWithoutDragging.eliminarMesas")}
                  </DangerButton>
                </ActionButtons>
              </MainActions>

              {tablesInSelectedZone.length === 0 ? (
                <EmptyState>
                  {t("ui.components.tablesWithoutDragging.semMesasNestaSala")}
                </EmptyState>
              ) : (
                <TableListScroll>
                  <TableList>
                    {tablesInSelectedZone.map((table) => (
                      <TableRowCard key={table.id}>
                        <TableRowGrid>
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                              }}
                            >
                              {table.table_name || t("ui.i18nFinalCleanup.unnamed")}
                            </div>
                            <SmallInfo>
                              {table.round_or_square === "round"
                                ? t("ui.components.tablesWithoutDragging.redonda")
                                : t("ui.components.tablesWithoutDragging.quadrada")}
                            </SmallInfo>
                          </div>

                          <div>
                            <div>
                              {table.number_of_seats || 0}{" "}
                              {t("ui.components.tablesWithoutDragging.lugares")}
                            </div>
                          </div>

                          <DeleteCell>
                            <DeleteIconButton
                              type="button"
                              aria-label={t("ui.components.tablesWithoutDragging.eliminarMesa")}
                              onClick={() =>
                                setConfirmDelete({
                                  show: true,
                                  tableId: table.id,
                                  label:
                                    table.table_name ||
                                    t("ui.i18nFinalCleanup.unnamed"),
                                })
                              }
                            >
                              <CloseIcon
                                style={{
                                  right: "0",
                                  position: "relative",
                                  color: "currentColor",
                                }}
                              />
                            </DeleteIconButton>
                          </DeleteCell>
                        </TableRowGrid>
                      </TableRowCard>
                    ))}
                  </TableList>
                </TableListScroll>
              )}
            </>
          ) : (
            <EmptyState>
              {t(
                "ui.components.tablesWithoutDragging.aindaNaoExistemSalasCriaUma",
              )}
            </EmptyState>
          )}
        </CardBodyBox>
      </StyledCard>

      {/* ---- Create Tables ---- */}
      <StyledModal
        show={showCreateTables}
        onHide={() => setShowCreateTables(false)}
        title={t("ui.components.tablesWithoutDragging.adicionarMesas2")}
        footer={
          <>
            <ModalFooterButton
              onClick={() => setShowCreateTables(false)}
              disabled={isCreating}
            >
              {t("ui.components.tablesWithoutDragging.cancelar")}
            </ModalFooterButton>
            <ModalFooterPrimaryButton
              onClick={handleCreateTables}
              disabled={!canCreateTables || isCreating}
            >
              {isCreating ? t("ui.i18nCleanup2.aCriar") : t("common.save")}
            </ModalFooterPrimaryButton>
          </>
        }
      >
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              {t("ui.components.tablesWithoutDragging.numeroDeMesasAAdicionar")}
            </Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={numTablesToAdd}
              onChange={(e) => setNumTablesToAdd(e.target.value)}
              placeholder={t("ui.components.tablesWithoutDragging.ex6")}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t("ui.components.tablesWithoutDragging.numeroDeLugaresPorMesa")}
            </Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={numSeatsPerTable}
              onChange={(e) => setNumSeatsPerTable(e.target.value)}
              placeholder={t("ui.components.tablesWithoutDragging.ex4")}
            />
          </Form.Group>

          <div
            style={{
              fontSize: "0.85rem",
              color: "#666",
            }}
          >
            {t(
              "ui.components.tablesWithoutDragging.posteriormentePodesAlterarONumeroDe",
            )}
          </div>
        </Form>
      </StyledModal>

      {/* ---- Delete Tables ---- */}
      <StyledModal
        show={showDeleteTables}
        onHide={() => setShowDeleteTables(false)}
        title={t("ui.components.tablesWithoutDragging.eliminarMesas2")}
        footer={
          <>
            <ModalFooterButton
              onClick={() => setShowDeleteTables(false)}
              disabled={isDeleting}
            >
              {t("ui.components.tablesWithoutDragging.cancelar2")}
            </ModalFooterButton>
            <ModalFooterDangerButton
              onClick={handleDeleteTables}
              disabled={
                isDeleting ||
                !restaurants?.id ||
                !selectedPlace ||
                !Number(numTablesToDelete) ||
                Number(numTablesToDelete) < 1
              }
            >
              {isDeleting ? t("ui.i18nCleanup2.aEliminar") : t("ui.components.tablesWithoutDragging.eliminar")}
            </ModalFooterDangerButton>
          </>
        }
      >
        <div
          className="mb-2"
          style={{
            fontSize: "0.9rem",
            color: "#555",
          }}
        >
          {t("ui.components.tablesWithoutDragging.salaSelecionada")}
          <b>{selectedZone?.place_of_table || "-"}</b>
          <br />
          {t("ui.components.tablesWithoutDragging.mesasActuaisNestaSala")}
          <b>{tablesInSelectedZone.length}</b>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>
            {t("ui.components.tablesWithoutDragging.numeroDeMesasAEliminar")}
          </Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={numTablesToDelete}
            onChange={(e) => setNumTablesToDelete(e.target.value)}
            placeholder={t("ui.components.tablesWithoutDragging.ex1")}
          />
          <Form.Text muted>
            {t(
              "ui.components.tablesWithoutDragging.seraoRemovidasAsUltimasNMesas",
            )}
          </Form.Text>
        </Form.Group>
      </StyledModal>

      {/* ---- Create Zone ---- */}
      <StyledModal
        show={showCreateZone}
        onHide={() => setShowCreateZone(false)}
        title={t("ui.components.tablesWithoutDragging.novaSala2")}
        footer={
          <>
            <ModalFooterButton
              onClick={() => setShowCreateZone(false)}
              disabled={isCreatingZone}
            >
              {t("ui.components.tablesWithoutDragging.cancelar3")}
            </ModalFooterButton>
            <ModalFooterPrimaryButton
              onClick={handleCreateZone}
              disabled={isCreatingZone || !zoneName.trim()}
            >
              {isCreatingZone ? t("ui.i18nCleanup2.aCriar") : t("common.save")}
            </ModalFooterPrimaryButton>
          </>
        }
      >
        <Form.Group>
          <Form.Label>
            {t("ui.components.tablesWithoutDragging.nomeDaSala")}
          </Form.Label>
          <Form.Control
            type="text"
            value={zoneName}
            onChange={(e) => setZoneName(e.target.value)}
            placeholder={t(
              "ui.components.tablesWithoutDragging.exSalaPrincipal",
            )}
          />
        </Form.Group>
      </StyledModal>

      {/* ---- Delete Zone ---- */}
      <StyledModal
        show={showDeleteZone}
        onHide={() => setShowDeleteZone(false)}
        title={t("ui.components.tablesWithoutDragging.eliminarSala2")}
        footer={
          <>
            <ModalFooterButton
              onClick={() => setShowDeleteZone(false)}
              disabled={isDeletingZone}
            >
              {t("ui.components.tablesWithoutDragging.cancelar4")}
            </ModalFooterButton>
            <ModalFooterDangerButton
              onClick={handleDeleteZone}
              disabled={isDeletingZone || !selectedPlace}
            >
              {isDeletingZone ? t("ui.i18nCleanup2.aEliminar") : t("ui.components.tablesWithoutDragging.eliminar")}
            </ModalFooterDangerButton>
          </>
        }
      >
        {t(
          "ui.components.tablesWithoutDragging.temACertezaQuePretendeEliminar",
        )}{" "}
        <b>{selectedZone?.place_of_table || "-"}</b>?
        <br />
        {t("ui.components.tablesWithoutDragging.mesasNestaSala")}
        <b>{tablesInSelectedZone.length}</b>
        <div
          style={{
            fontSize: "0.85rem",
            color: "#666",
            marginTop: 8,
          }}
        >
          {t("ui.components.tablesWithoutDragging.estaAcaoNaoPodeSerDesfeita")}
        </div>
      </StyledModal>

      {/* ---- Bulk Edit List ---- */}
      <StyledModal
        show={showBulkEditor}
        onHide={requestCloseBulkEditor}
        $wide
        title={t("ui.components.tablesWithoutDragging.editarListaDeMesas")}
        footer={
          <>
            <ModalFooterButton
              onClick={requestCloseBulkEditor}
              disabled={isBulkEditorSaving}
            >
              {t("ui.components.tablesWithoutDragging.cancelar5")}
            </ModalFooterButton>

            <ModalFooterPrimaryButton
              onClick={() => saveBulkEditor({ closeAfterSave: true })}
              disabled={
                isBulkEditorSaving || !bulkRows.length || !userInfo?.token
              }
            >
              {isBulkEditorSaving ? t("ui.i18nCleanup2.aGuardar2") : t("ui.screens.reservationScreen.guardarAlteracoes")}
            </ModalFooterPrimaryButton>
          </>
        }
      >
        <SectionCard>
          <EditorSettingsGrid>
            <DropdownField>
              <FieldLabel>
                {t("ui.components.tablesWithoutDragging.sala")}
              </FieldLabel>
              <DropDownButton
                options={zoneDropdownOptions}
                initialOption={bulkSelectedZone?.place_of_table || ""}
                handleOptionChange={handleBulkRoomChange}
              />
            </DropdownField>

            {/* TEMPORARILY HIDDEN: Modo de atribuição de mesas.
            <DropdownField>
              <FieldLabel>{t("ui.i18nCleanup.automaticAssignmentMode")}</FieldLabel>
              <DropDownButton
                options={assignmentModeOptions}
                initialOption={
                  draftAutoSetTables
                    ? assignmentModeOptions[0]
                    : assignmentModeOptions[1]
                }
                handleOptionChange={(option) =>
                  setDraftAutoSetTables(option === assignmentModeOptions[0])
                }
              />
              <HelpText>
                {draftAutoSetTables
                  ? t("ui.i18nCleanup2.oSistemaPodeCombinarQuaisquerMesasLivresParaEncontrarAMelhorOpcao")
                  : t("ui.i18nCleanup2.oSistemaSoUsaMesasIndividuaisOuGruposDeMesasConfigurados")}
              </HelpText>
            </DropdownField>
            */}
          </EditorSettingsGrid>
        </SectionCard>

        {/* TEMPORARILY HIDDEN: Configurar grupos / junções.
        <GroupToolbar>
          <div>
            <RoomTitle>{t("ui.screens.restaurantZonesTablesManagement.gruposDeMesas")}</RoomTitle>
            <HelpText>
              {isConnectionConfigMode && currentConnectionTable
                ? t("ui.i18nFinalCleanup.configuringTableConnections", { table: currentConnectionTable.table_name || currentConnectionTable.table_number })
                : draftAutoSetTables
                  ? t("ui.i18nCleanup2.asJuncoesFicamGuardadasMasSoSaoUsadasQuandoEscolheresGruposDeMesasFixo")
                  : t("ui.i18nCleanup2.consultaAsJuncoesAtuaisOuConfiguraOsGruposDeMesasFixos")}
            </HelpText>
          </div>

          {!isConnectionConfigMode ? (
            <CompactButton
              onClick={handleStartGroupConfig}
              disabled={!bulkRows.length}
            >
              {t("ui.i18nCleanup.configureGroups")}
            </CompactButton>
          ) : (
            <GroupToolbarActions>
              <DropdownField>
                <FieldLabel>{t("ui.i18nCleanup.tableBeingConfigured")}</FieldLabel>
                <DropDownButton
                  options={bulkTableDropdownOptions}
                  initialOption={getBulkTableDropdownLabel(
                    currentConnectionTable,
                  )}
                  handleOptionChange={handleConnectionTableChange}
                />
              </DropdownField>

              <CompactButton
                onClick={() => {
                  setIsConnectionConfigMode(false);
                  setCurrentConnectionTableId(null);
                  setSelectedConnectionIds([]);
                }}
              >
                {t("ui.i18nCleanup.finishConfiguration")}
              </CompactButton>
            </GroupToolbarActions>
          )}
        </GroupToolbar>
        */}

        <BulkTableScroll>
          <BulkHeader>
            {/* TEMPORARILY HIDDEN: Mesa number column.
            <div>{t("ui.components.tablesWithoutDragging.mesa2")}</div>
            */}
            <div>{t("ui.components.tablesWithoutDragging.nomeDaMesa")}</div>
            <div>{t("ui.components.tablesWithoutDragging.lugares2")}</div>
            {/* TEMPORARILY HIDDEN: Formato + Junções columns.
            <div>{t("ui.components.tablesWithoutDragging.formato")}</div>
            <div>{t("ui.i18nCleanup.connections")}</div>
            */}
            <div>{t("ui.components.tablesWithoutDragging.eliminar")}</div>
          </BulkHeader>

          {bulkRows.length === 0 ? (
            <BulkRows>
              <EmptyState>
                {t("ui.components.tablesWithoutDragging.semMesasNestaSala2")}
              </EmptyState>
            </BulkRows>
          ) : (
            <BulkRows>
              {bulkRows.map((row) => (
                <BulkRow key={row.id}>
                  {/* TEMPORARILY HIDDEN: Mesa number.
                  <div style={{ fontWeight: 800, color: palette.ink }}>
                    {row.table_number || "-"}
                  </div>
                  */}

                  <Form.Control
                    type="text"
                    value={row.table_name || ""}
                    onChange={(e) =>
                      onBulkRowChange(row.id, "table_name", e.target.value)
                    }
                    placeholder={t(
                      "ui.components.tablesWithoutDragging.nomeDaMesa2",
                    )}
                  />

                  <Form.Control
                    type="number"
                    min={1}
                    value={row.number_of_seats ?? ""}
                    onChange={(e) =>
                      onBulkRowChange(row.id, "number_of_seats", e.target.value)
                    }
                    placeholder={t(
                      "ui.components.tablesWithoutDragging.lugares3",
                    )}
                  />

                  {/* TEMPORARILY HIDDEN: Formato.
                  <DropdownField>
                    <DropDownButton
                      options={tableShapeOptions}
                      initialOption={
                        row.round_or_square === "round"
                          ? tableShapeOptions[1]
                          : tableShapeOptions[0]
                      }
                      handleOptionChange={(option) =>
                        onBulkRowChange(
                          row.id,
                          "round_or_square",
                          option === tableShapeOptions[1] ? "round" : "square",
                        )
                      }
                    />
                  </DropdownField>
                  */}

                  {/* TEMPORARILY HIDDEN: Junções.
                  <ConnectionsCell>
                    {!isConnectionConfigMode ? (
                      <span>{getConnectedTableNames(row.id)}</span>
                    ) : Number(currentConnectionTableId) === Number(row.id) ? (
                      <span
                        style={{
                          color: palette.sageDark,
                          fontWeight: 700,
                        }}
                      >
                        {t("ui.components.tablesWithoutDragging.mesaAtual")}
                      </span>
                    ) : currentConnectionTableId ? (
                      <>
                        <Form.Check
                          type="checkbox"
                          label={t(
                            "ui.components.tablesWithoutDragging.podeJuntar",
                          )}
                          checked={selectedConnectionIds.includes(row.id)}
                          onChange={() => toggleConnectionTable(row.id)}
                        />

                        <TextButton
                          variant="link"
                          onClick={() => startEditingConnections(row)}
                        >
                          {t("ui.components.tablesWithoutDragging.editarEsta")}
                        </TextButton>
                      </>
                    ) : (
                      <span style={{ color: palette.muted }}>
                        {t("ui.i18nCleanup.chooseATable")}
                      </span>
                    )}
                  </ConnectionsCell>
                  */}

                  <div>
                    <DeleteIconButton
                      type="button"
                      aria-label={t("ui.components.tablesWithoutDragging.eliminarMesa")}
                      onClick={() =>
                        setConfirmDelete({
                          show: true,
                          tableId: row.id,
                          label:
                            row.table_number || row.table_name || `#${row.id}`,
                        })
                      }
                    >
                      <CloseIcon
                        style={{
                          right: "0",
                          position: "relative",
                          color: "currentColor",
                        }}
                      />
                    </DeleteIconButton>
                  </div>
                </BulkRow>
              ))}

              <div style={{ display: "flex", paddingTop: 4 }}>
                <AddFilterBox
                  type="button"
                  onClick={() => setShowCreateTables(true)}
                >
                  <span style={{ fontSize: 18, lineHeight: "18px" }}>＋</span>
                  <span>
                    {t("ui.components.tablesWithoutDragging.adicionarMesas3")}
                  </span>
                </AddFilterBox>
              </div>
            </BulkRows>
          )}
        </BulkTableScroll>
      </StyledModal>

      <StyledModal
        show={showUnsavedChangesModal}
        onHide={() => setShowUnsavedChangesModal(false)}
        title={t("ui.i18nCleanup.saveChangesQuestion")}
        footer={
          <>
            <ModalFooterButton
              onClick={() => setShowUnsavedChangesModal(false)}
              disabled={isBulkEditorSaving}
            >
              {t("ui.components.changeTables.continuarEdicao")}
            </ModalFooterButton>

            <ModalFooterDangerButton
              onClick={forceCloseBulkEditor}
              disabled={isBulkEditorSaving}
            >
              {t("ui.components.changeTables.sairSemGuardar")}
            </ModalFooterDangerButton>

            <ModalFooterPrimaryButton
              onClick={() => saveBulkEditor({ closeAfterSave: true })}
              disabled={isBulkEditorSaving || !userInfo?.token}
            >
              {isBulkEditorSaving ? t("ui.i18nCleanup2.aGuardar2") : t("ui.i18nCleanup2.guardarESair")}
            </ModalFooterPrimaryButton>
          </>
        }
      >
        {t("ui.i18nCleanup.unsavedTableChangesConfirm")}
      </StyledModal>

      {/* ---- Confirm single delete ---- */}
      <StyledModal
        show={confirmDelete.show}
        onHide={() =>
          setConfirmDelete({
            show: false,
            tableId: null,
            label: "",
          })
        }
        title={t("ui.components.tablesWithoutDragging.eliminarMesa")}
        footer={
          <>
            <ModalFooterButton
              onClick={() =>
                setConfirmDelete({
                  show: false,
                  tableId: null,
                  label: "",
                })
              }
              disabled={deletingRowId === confirmDelete.tableId}
            >
              {t("ui.components.tablesWithoutDragging.cancelar6")}
            </ModalFooterButton>
            <ModalFooterDangerButton
              onClick={async () => {
                if (!confirmDelete.tableId) return;
                await handleDeleteSingleTable(confirmDelete.tableId);
                setConfirmDelete({
                  show: false,
                  tableId: null,
                  label: "",
                });
              }}
              disabled={deletingRowId === confirmDelete.tableId}
            >
              {deletingRowId === confirmDelete.tableId
                ? t("ui.i18nCleanup2.aEliminar")
                : t("ui.components.tablesWithoutDragging.eliminar")}
            </ModalFooterDangerButton>
          </>
        }
      >
        {t(
          "ui.components.tablesWithoutDragging.tensACertezaQuePretendesEliminar",
        )}
      </StyledModal>
    </Container>
  );
};
export default TablesWithoutDragging;
